import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import toast from "react-hot-toast";
import "./WishlistPage.css";
import Loader from "../layouts/components/Loader";
import AuthModal from "../layouts/components/AuthModal";

const API_BASE = "https://api.mariamlyluxe.com/public";

const getToken = () =>
  localStorage.getItem("token") ||
  localStorage.getItem("auth_token") ||
  localStorage.getItem("access_token") ||
  "";

function money(n = 0) {
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 2 }).format(n || 0);
  } catch {
    return `$${(n || 0).toFixed(2)}`;
  }
}

function fileUrl(path = "") {
  const clean = String(path || "").replace(/^\/+/, "");
  if (!clean) return "";
  if (clean.startsWith("http")) return clean;
  if (clean.startsWith("storage/")) return `${API_BASE}/${clean}`;
  return `${API_BASE}/storage/${clean}`;
}

function CartIcon({ size = 18, strokeWidth = 1.8, color = "#fff" }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <line x1="12" y1="4" x2="12" y2="8" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
      <line x1="10" y1="6" x2="14" y2="6" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" />
      <path
        d="M3 3h2l.4 2M7 13h10l4-8H5.4m0 0L7 13m0 0l-2.5 5M7 13l2.5 5m0 0h9"
        stroke={color}
        strokeWidth={strokeWidth}
        fill="none"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

function HeartIcon({ size = 18, strokeWidth = 1.8, color = "#000", filled = false }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"
        stroke={color}
        strokeWidth={strokeWidth}
        fill={filled ? color : "none"}
        strokeLinecap="round"
        strokeLinejoin="round"
      />
    </svg>
  );
}

export default function WishlistPage() {
  const [wishlistItems, setWishlistItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [busyId, setBusyId] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);

  const loadWishlist = async () => {
    const token = getToken();
    if (!token) {
      setLoading(false);
      setWishlistItems([]);
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await fetch(`${API_BASE}/api/wishlist`, {
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data?.message || "Failed to load wishlist");
      }

      // Normalize wishlist items like other components
      const items = data?.wishlist || data?.data || [];
      const norm = items.map((item) => {
        const product = item.product || item;
        const firstImg = Array.isArray(product.images) && product.images.length ? product.images[0].path : null;
        return {
          id: product.id,
          name: product.name ?? product.title ?? "Product",
          price: Number(product.selling_price ?? product.price ?? product.regular_price ?? 0),
          image_url:
            product.image_url ? product.image_url :
            product.image     ? fileUrl(product.image) :
            firstImg    ? fileUrl(firstImg) :
            null
        };
      });

      setWishlistItems(norm);
    } catch (e) {
      setError(e.message || "Failed to load wishlist");
      setWishlistItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadWishlist();
  }, []);

  const handleAddToCart = async (productId, quantity = 1) => {
    const token = getToken();
    if (!token) {
      setShowAuthModal(true);
      return;
    }

    try {
      setBusyId(productId);
      const response = await fetch(`${API_BASE}/api/cart`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ product_id: productId, quantity }),
      });

      const data = await response.json();
      if (!response.ok || data?.success === false) {
        throw new Error(data?.message || "Failed to add to cart");
      }

      toast.success("Product added to cart");
    } catch (err) {
      toast.error(err.message || "Could not add to cart");
    } finally {
      setBusyId(null);
    }
  };

  const handleRemoveFromWishlist = async (productId) => {
    const token = getToken();
    if (!token) return;

    try {
      setBusyId(productId);
      const response = await fetch(`${API_BASE}/api/wishlist/${productId}`, {
        method: "DELETE",
        headers: {
          Accept: "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error("Failed to remove from wishlist");
      }

      // Remove from local state
      setWishlistItems(prev => prev.filter(item => item.id !== productId));
      toast.success("Removed from wishlist");
    } catch (err) {
      toast.error(err.message || "Could not remove from wishlist");
    } finally {
      setBusyId(null);
    }
  };

  const handleLoginSuccess = () => {
    setShowAuthModal(false);
    loadWishlist();
  };

  // Redirect if not authenticated
  if (!getToken()) {
    return (
      <section className="wishlist-page">
        <div className="wishlist-container">
          <h1>My Wishlist</h1>
          <div className="wishlist-error">
            <p>You must be logged in to view your wishlist.</p>
            <button 
              className="wishlist-btn wishlist-btn-primary"
              onClick={() => setShowAuthModal(true)}
            >
              Login / Register
            </button>
          </div>
        </div>
        
        <AuthModal
          isOpen={showAuthModal}
          onClose={() => setShowAuthModal(false)}
          onLoginSuccess={handleLoginSuccess}
        />
      </section>
    );
  }

  return (
    <>
      <section className="wishlist-page">
        <div className="wishlist-container">
          <div className="wishlist-header">
            <h1>My Wishlist</h1>
            <p>Products you've saved for later</p>
          </div>

          {loading ? (
            <div className="wishlist-loading">
              <Loader />
            </div>
          ) : error ? (
            <div className="wishlist-error">
              <p>{error}</p>
              <button 
                className="wishlist-btn wishlist-btn-outline"
                onClick={loadWishlist}
              >
                Try Again
              </button>
            </div>
          ) : wishlistItems.length === 0 ? (
            <div className="wishlist-empty">
              <div className="wishlist-empty-icon">💝</div>
              <h2>Your wishlist is empty</h2>
              <p>Start exploring our products and add your favorites to your wishlist.</p>
              <Link to="/shop" className="wishlist-btn wishlist-btn-primary">
                Start Shopping
              </Link>
            </div>
          ) : (
            <div className="wishlist-grid">
              {wishlistItems.map((product) => (
                <article key={product.id} className="wishlist-card">
                  <div className="wishlist-img">
                    {product.image_url && (
                      <img 
                        src={product.image_url} 
                        alt={product.name} 
                        loading="lazy" 
                        decoding="async" 
                      />
                    )}
                  </div>
                  <div className="wishlist-body">
                    <Link 
                      to={`/product/${product.id}`} 
                      className="wishlist-name"
                    >
                      {product.name}
                    </Link>
                    <div className="wishlist-price">{money(product.price)}</div>
                    <div className="wishlist-actions">
                      <button
                        className="wishlist-cart"
                        aria-label={busyId === product.id ? "Adding..." : "Add to cart"}
                        onClick={() => handleAddToCart(product.id, 1)}
                        disabled={busyId === product.id}
                        title={busyId === product.id ? "Adding..." : "Add to cart"}
                      >
                        {busyId === product.id ? <Loader size={18} /> : <CartIcon />}
                      </button>
                      <button
                        className="wishlist-remove"
                        aria-label="Remove from wishlist"
                        onClick={() => handleRemoveFromWishlist(product.id)}
                        disabled={busyId === product.id}
                        title="Remove from wishlist"
                      >
                        <HeartIcon filled={true} />
                      </button>
                    </div>
                  </div>
                </article>
              ))}
            </div>
          )}
        </div>
      </section>

      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        onLoginSuccess={handleLoginSuccess}
      />
    </>
  );
}